import { Injectable } from '@angular/core';
import { IFormGroup } from '@shared-libs/interfaces';
import { Action, NavigationDataKey, UserPermissions, WaitListItemStatus } from '@shared-libs/enums';
import { NavigationService } from '@shared-services/navigation.service';
import { formIsValid, translate } from '@shared-libs/helpers';
import { IWaitListItem } from '@internal-models/wait-list-item.model';
import { WaitListProvider } from '@internal-providers/wait-list.provider';
import { WaitListItemFormFactory } from '@internal-factories/forms/wait-list-item-form-factory';
import { IEventType } from '@internal-models/event-type.model';
import { EventTypeProvider } from '@internal-providers/event-type.provider';
import { EventAttendanceType, EventLocationType, IEvent } from '@internal-models/event.model';
import { InternalNavigationType } from '@shared-directives/navigate.directive';

@Injectable({ providedIn: 'root' })
export class WaitListDetailsController {
	public action: Action;
	public waitListItem: IWaitListItem;
	public waitListItemForm: IFormGroup<IWaitListItem>;
	public eventTypes: Array<IEventType>;
	public waitListItemStatuses: Array<{ value: WaitListItemStatus; label: string }>;

	public readonly Action: typeof Action = Action;
	public readonly EventAttendanceType: typeof EventAttendanceType = EventAttendanceType;
	public readonly EventLocationType: typeof EventLocationType = EventLocationType;
	public readonly InternalNavigationType: typeof InternalNavigationType = InternalNavigationType;
	public readonly NavigationDataKey: typeof NavigationDataKey = NavigationDataKey;
	public readonly UserPermissions: typeof UserPermissions = UserPermissions;

	constructor(
		private readonly navigationService: NavigationService,
		private readonly provider: WaitListProvider,
		private readonly eventTypeProvider: EventTypeProvider
	) {
		this.getEventTypes();
		this.getWaitListItemStatuses();
	}

	public async onInit(): Promise<void> {
		this.action = this.navigationService.getNavigationData(NavigationDataKey.action);
		this.waitListItem = this.navigationService.getNavigationData(NavigationDataKey.waitListItem);

		const event = this.navigationService.getNavigationData(NavigationDataKey.event);
		if (this.waitListItem) {
			this.waitListItem = await this.provider.getOne(this.waitListItem.id);
		}
		this.initializeForm(this.waitListItem, event);
	}

	public onDestroy(): void {
		this.waitListItemForm = null;
	}

	public initializeForm(waitListItem: IWaitListItem, event: IEvent): void {
		this.waitListItemForm = WaitListItemFormFactory.create(this.action, waitListItem, event);
	}

	public async createOrUpdate(): Promise<void> {
		if (formIsValid(this.waitListItemForm)) {
			const waitListItem: IWaitListItem = WaitListItemFormFactory.getValue(
				this.action,
				this.waitListItemForm
			) as IWaitListItem;

			this.action === Action.CREATE
				? await this.provider.create(waitListItem)
				: await this.provider.update(this.waitListItem.id, waitListItem);

			this.navigationService.navigateBack({ action: this.action });
		}
	}

	public close(): void {
		this.navigationService.navigateBack();
	}

	/**
	 * Get the eventTypes from the API
	 */
	private async getEventTypes(): Promise<void> {
		this.eventTypes = await this.eventTypeProvider.getAll();
	}

	private getWaitListItemStatuses(): void {
		this.waitListItemStatuses = Object.keys(WaitListItemStatus).map((key) => ({
			value: WaitListItemStatus[key],
			label: translate(`WAIT_LIST.STATUS.${key.toUpperCase()}`),
		}));
	}
}
